<template>
  <g class="AreaChart" :transform="`translate(${left}, ${top})`">
    <g
      class="AreaChart-content"
      :transform="`translate(${viewboxPosition.x + paddingX}, ${paddingY})`"
    >
      <template v-if="showArea">
        <datalas-area-path
          v-for="(path, i) in paths"
          :key="'area' + i"
          v-bind="path"
        />
      </template>

      <template v-if="showLine">
        <datalas-line-path
          v-for="(path, i) in paths"
          :key="'line' + i"
          v-bind="path"
          curve
        />
      </template>

      <template v-if="showDots">
        <datalas-dot-path
          v-for="(path, i) in paths"
          :key="'dot' + i"
          v-bind="path"
          @datum:mouseover="$emit('datum:mouseover', $event)"
          @datum:mouseout="$emit('datum:mouseout', $event)"
          @datum:click="$emit('datum:click', $event)"
          showOnHover
        />
      </template>

      <datalas-labels
        v-if="showLabels"
        :labels="labels"
        :prefix="labelPrefix"
        :suffix="labelSuffix"
        :format="labelFormat"
        :font-size="labelFontSize"
        :detect-collisions="noLabelCollisions"
        :filters="labelFilters"
        :offset-x="labelOffsetX"
        :offset-y="labelOffsetY"
      />
    </g>

    <teleport v-if="gridMounted" :to="`#${gridKey}`">
      <DatalasFlexibleGrid
        v-bind="gridLayout"
        :viewboxPosition="viewboxPosition"
        :left="left"
        :chartHeight="widgetContentWrapperRect.height"
        :grid-key="gridKey"
        @update:axisTickSize="$emit('update:axisTickSize', $event)"
        @update:axisSize="$emit('update:axisSize', $event)"
      />
    </teleport>
  </g>
</template>

<script>
import Area from './area.vue';
import DatalasFlexibleGrid from './flexible-grid.vue';

export default {
  name: 'DatalasFlexableArea',
  components: { DatalasFlexibleGrid },
  extends: Area,

  emits: ['update:axisTickSize', 'update:layoutPaddings', 'update:axisSize'],
  props: {
    viewboxPosition: Object,
    default: () => ({ x: 0, y: 0 }),

    widgetContentWrapperRect: {
      type: Object,
      default: () => ({ width: 0, height: 0 }),
    },
  },
  computed: {
    gridLayout() {
      const grid = { ...this.grid };
      grid.axes = grid.axes.map((axis) => {
        const props = { ...axis.props };

        if (props.orient === 'bottom')
          props.labelAngle = this.isLabelIsLong(axis.props.scale, 10)
            ? props.labelAngle || -45
            : props.labelAngle;

        const x = this.dimensions.find((d) => d.dimension === 'x').transform;
        props.scale = props.scale === x ? this.xScale : this.yScale;
        props.height = this.height;
        props.width = this.width;
        return { ...axis, props };
      });
      return grid;
    },
    paths() {
      return this.nested.map((group, i) => {
        const fill = this.fillScale(group.key);
        const stack = this.stacked[i];

        return {
          fill,
          stroke: fill,
          data: group.values.map((d, j) => ({
            ...d, // TODO: don't mix data with presentation
            y0: stack[j] ? stack[j][0] : 0,
            y1: stack[j] ? stack[j][1] : 0,
            datum: { ...d, fill },
          })),
          xAccessor: this.xAccessor,
          y0Accessor: (d) =>
            this.isStacked ? d.y0 : this.yTransform.options.min,
          yAccessor: (d) => (this.isStacked ? d.y1 : this.yAccessor(d)),
          xScale: this.xScale,
          yScale: this.yScale,
          bandwidth: this.bandwidth,
          isSideways: this.isSideways,
          strokeWidth: this.dotSize / 7.0,
          lineSize: this.lineSize / 7.0,
        };
      });
    },
  },

  methods: {
    xAccessor(item) {
      return item[this.xTransform.field];
    },
    yAccessor(item) {
      return item[this.yTransform.field];
    },
    fillAccessor(item) {
      return item[this.fillTransform.field];
    },

    getDomain(accessor, options) {
      if (options.type === 'continuous') {
        const { min, max } = options;
        return [
          min === undefined || min === null
            ? this.$d3.min(this.data, accessor)
            : min,
          max === undefined || max === null
            ? this.$d3.max(this.data, accessor)
            : max,
        ];
      }

      // Assume categorical scale
      return Object.keys(
        this.data
          .map(accessor)
          .reduce((acc, x) => Object.assign(acc, { [x]: true }), {}),
      );
    },

    isLabelIsLong(value, numberOfCharacters) {
      if (this.transforms[value]) {
        const fieldName = this.transforms[value].field;
        const res = this.data.some(
          (item) => item[fieldName].toString().length >= numberOfCharacters,
        );
        return res;
      }
      return undefined;
    },
  },
  mounted() {
    const paddings = {
      top: this.top,
      right: this.right,
      bottom: this.bottom,
      left: this.left,
    };

    if (paddings) this.$emit('update:layoutPaddings', paddings);
  },
};
</script>
